import { applyHomography, type Homography3x3 } from './Homography';
import type { Trajectory } from './TrajectoryStore';

export interface HeatmapOptions {
  gridWidth?: number;
  gridHeight?: number;
  radius?: number;
  opacity?: number;
  threshold?: number;
}

const DEFAULT_WIDTH = 64;
const DEFAULT_HEIGHT = 36;

export class HeatmapRenderer {
  private gridW: number;
  private gridH: number;
  private radius: number;
  private opacity: number;
  private threshold: number;
  private grid: Float32Array;

  constructor(opts: HeatmapOptions = {}) {
    this.gridW = opts.gridWidth ?? DEFAULT_WIDTH;
    this.gridH = opts.gridHeight ?? DEFAULT_HEIGHT;
    this.radius = opts.radius ?? 2;
    this.opacity = opts.opacity ?? 0.55;
    this.threshold = opts.threshold ?? 0.04;
    this.grid = new Float32Array(this.gridW * this.gridH);
  }

  setOpacity(value: number): void {
    this.opacity = Math.max(0, Math.min(1, value));
  }

  setRadius(value: number): void {
    this.radius = Math.max(0, Math.floor(value));
  }

  accumulate(trajectories: readonly Trajectory[]): void {
    this.grid.fill(0);
    for (const traj of trajectories) {
      if (traj.class === 'ball') continue;
      for (const p of traj.points) {
        this.addSample(p.x, p.y, 1);
      }
    }
    if (this.radius > 0) this.blur(this.radius);
  }

  accumulateOnCourt(
    trajectories: readonly Trajectory[],
    H: Homography3x3,
    courtWidthMeters: number,
    courtHeightMeters: number,
  ): void {
    this.grid.fill(0);
    for (const traj of trajectories) {
      if (traj.class === 'ball') continue;
      for (const p of traj.points) {
        const proj = applyHomography({ x: p.x, y: p.y }, H);
        if (!Number.isFinite(proj.x) || !Number.isFinite(proj.y)) continue;
        const nx = proj.x / courtWidthMeters;
        const ny = proj.y / courtHeightMeters;
        if (nx < 0 || nx >= 1 || ny < 0 || ny >= 1) continue;
        this.addSample(nx, ny, 1);
      }
    }
    if (this.radius > 0) this.blur(this.radius);
  }

  addSample(nx: number, ny: number, weight: number): void {
    if (nx < 0 || nx >= 1 || ny < 0 || ny >= 1) return;
    const gx = Math.min(this.gridW - 1, Math.floor(nx * this.gridW));
    const gy = Math.min(this.gridH - 1, Math.floor(ny * this.gridH));
    this.grid[gy * this.gridW + gx] += weight;
  }

  private blur(radius: number): void {
    const next = new Float32Array(this.grid.length);
    for (let y = 0; y < this.gridH; y += 1) {
      for (let x = 0; x < this.gridW; x += 1) {
        let sum = 0;
        let count = 0;
        for (let dy = -radius; dy <= radius; dy += 1) {
          for (let dx = -radius; dx <= radius; dx += 1) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx < 0 || nx >= this.gridW || ny < 0 || ny >= this.gridH) continue;
            sum += this.grid[ny * this.gridW + nx];
            count += 1;
          }
        }
        next[y * this.gridW + x] = count > 0 ? sum / count : 0;
      }
    }
    this.grid = next;
  }

  draw(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    let maxVal = 0;
    for (let i = 0; i < this.grid.length; i += 1) {
      if (this.grid[i] > maxVal) maxVal = this.grid[i];
    }
    if (maxVal <= 0) return;
    const cellW = width / this.gridW;
    const cellH = height / this.gridH;
    for (let gy = 0; gy < this.gridH; gy += 1) {
      for (let gx = 0; gx < this.gridW; gx += 1) {
        const v = this.grid[gy * this.gridW + gx] / maxVal;
        if (v < this.threshold) continue;
        ctx.fillStyle = heatColor(v, this.opacity);
        ctx.fillRect(gx * cellW, gy * cellH, cellW + 1, cellH + 1);
      }
    }
  }
}

export function heatColor(v: number, alpha = 0.6): string {
  const clamped = Math.max(0, Math.min(1, v));
  let r: number;
  let g: number;
  let b: number;
  if (clamped < 0.25) {
    const t = clamped / 0.25;
    r = 0;
    g = Math.round(60 + 100 * t);
    b = Math.round(180 + 60 * t);
  } else if (clamped < 0.5) {
    const t = (clamped - 0.25) / 0.25;
    r = 0;
    g = Math.round(160 + 60 * t);
    b = Math.round(240 * (1 - t) + 120 * t);
  } else if (clamped < 0.75) {
    const t = (clamped - 0.5) / 0.25;
    r = Math.round(255 * t);
    g = Math.round(220 - 40 * t);
    b = Math.round(120 * (1 - t));
  } else {
    const t = (clamped - 0.75) / 0.25;
    r = 255;
    g = Math.round(180 * (1 - t) + 40 * t);
    b = Math.round(0);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}
